// PlayerControls — custom Tailwind video controls overlay (M3 T8).
//
// Replaces the native `<video controls>` attribute on the watch page. Drives
// the underlying `<video>` element imperatively through the `videoRef` the
// parent attaches; mirrors media state into React via event listeners so the
// icons/time reflect playback. The parent's `containerRef` scopes fullscreen
// and provides the mousemove surface for auto-hide.
//
// Scope: this component owns the control surface only. T9 added playback
// speed + global keyboard shortcuts; T10 adds resume seek + progress tracking;
// T11 adds queue skip controls.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  FullscreenEnterIcon,
  FullscreenExitIcon,
  GearIcon,
  NextIcon,
  PauseIcon,
  PlayIcon,
  PrevIcon,
  VolumeHighIcon,
  VolumeMuteIcon,
} from "./icons";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { Tooltip } from "./ui/tooltip";
import { formatTime } from "../lib/format";
import { isAssFormat, isImageFamily, subtitleLabel } from "../lib/subtitles";
import {
  getGlobalPrefs,
  getSubtitlePref,
  saveGlobalPrefs,
  saveSubtitlePref,
} from "../lib/player-db";
import type { SubtitleTrackDto } from "../api/types";
import type { HlsLevel } from "../hooks/use-hls-stream";

type PlayerControlsProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  subtitleTracks: SubtitleTrackDto[];
  /** Video ID used to key per-video subtitle preferences in IndexedDB. */
  videoId: string;
  /**
   * Notified whenever the active subtitle selection changes (M8 Task 004) —
   * including the IndexedDB restore on mount. The watch page mirrors this
   * to route an ASS selection to the libass canvas overlay. Optional: when
   * omitted, PlayerControls behaves exactly as before (no observer).
   */
  onActiveSubtitleChange?: (index: number) => void;
  /**
   * Optional queue skip controls (M3 T11). When `onPrev`/`onNext` are both
   * omitted, no skip buttons render. When provided, the buttons flank the
   * play/pause button (prev | play | next — YouTube pattern) and reflect
   * their `hasPrev`/`hasNext` disabled state. The watch page passes these
   * only when an ephemeral queue exists (PRD FR-10.2 / FR-10.5).
   */
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  /**
   * Optional HLS variant ladder + manual override (M7 T12). When `levels`
   * is non-empty, the controls render a quality menu (Auto + each rung)
   * and a resolution badge next to the time display. Omit for non-transcode
   * strategies (direct/proxy/cached) where ABR doesn't apply.
   */
  levels?: HlsLevel[];
  /** Active level index, or -1 for Auto. `null` until hls.js reports one. */
  currentLevel?: number | null;
  onLevelChange?: (level: number) => void;
  /**
   * True when the stream strategy is `transcode` (M8 Task 010). When true,
   * image-family subtitle tracks (PGS/VobSub) are selectable — clicking one
   * shows the burn-in confirmation dialog before re-requesting the transcode
   * with `?burn_subtitles=`. When false/omitted, image tracks render disabled
   * (burn-in is impossible without a transcode pipeline).
   */
  isTranscode?: boolean;
  /**
   * Fired when the user confirms the burn-in dialog for an image-family
   * track (M8 Task 010 FR-5.3). The watch page re-requests the master
   * playlist with `?burn_subtitles={trackId}` appended. Optional: when
   * omitted, image tracks are always disabled (no burn callback wired).
   */
  onConfirmBurnSubtitle?: (trackId: string) => void;
};

/** Inactivity delay before controls hide during playback. */
const AUTO_HIDE_MS = 3000;
/** Seconds the focused seek-slider arrow keys jump (ARIA role=slider step). */
const SEEK_STEP_SECONDS = 5;
/** Seconds the global seek shortcuts (←/→, j/l) jump. */
const SHORTCUT_SEEK_SECONDS = 10;
/** Volume step applied by the ↑/↓ shortcuts. */
const SHORTCUT_VOLUME_STEP = 0.05;
/** Available playback rates (FR-8.5). */
const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export function PlayerControls({
  videoRef,
  containerRef,
  subtitleTracks,
  videoId,
  onActiveSubtitleChange,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  levels,
  currentLevel,
  onLevelChange,
  isTranscode = false,
  onConfirmBurnSubtitle,
}: PlayerControlsProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [activeSubtitle, setActiveSubtitle] = useState(-1);
  // M8 Task 010: when non-null, the burn-in confirmation dialog is open
  // for the image-family track at this index. The user must confirm before
  // the selection applies and the parent re-requests the transcode.
  const [pendingBurnIndex, setPendingBurnIndex] = useState<number | null>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  // Task 013: one grouped Settings menu replaces the three popovers
  // (quality / speed / subtitles). Radio selections keep the menu open so
  // several settings can be adjusted in one sitting; Escape, the gear, or
  // a click outside close it.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // True while ABR (Auto) is the user's quality selection. `currentLevel`
  // reports the *actually playing* rung even in Auto (hls.js LEVEL_SWITCHED
  // fires either way), so the user's Auto-vs-manual choice is local UI
  // state here — the FR-4.3 "Auto (current: 1080p)" labeling needs both
  // facts. `null` = Auto; a number = the manually picked rung index.
  const [qualitySelection, setQualitySelection] = useState<number | null>(
    null,
  );
  const [showShortcuts, setShowShortcuts] = useState(false);

  // `isPlaying` is also kept in a ref so the auto-hide timer (created once on
  // mount) can read the latest play state without re-subscribing.
  const isPlayingRef = useRef(false);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // `showShortcuts` is mirrored into a ref for the same reason: the global
  // keydown listener (subscribed once) reads it to suspend media shortcuts
  // while the help overlay is open.
  const showShortcutsRef = useRef(false);
  useEffect(() => {
    showShortcutsRef.current = showShortcuts;
  }, [showShortcuts]);

  // M8 Task 004: keep the latest onActiveSubtitleChange in a ref so the
  // restore-on-mount effect (which doesn't depend on the callback) always
  // notifies the current observer without re-subscribing when the parent
  // passes a new closure.
  const onActiveSubtitleChangeRef = useRef(onActiveSubtitleChange);
  useEffect(() => {
    onActiveSubtitleChangeRef.current = onActiveSubtitleChange;
  });

  // M8 Task 010: keep the latest onConfirmBurnSubtitle in a ref for the
  // same reason — the confirm handler reads it without re-subscribing.
  const onConfirmBurnSubtitleRef = useRef(onConfirmBurnSubtitle);
  useEffect(() => {
    onConfirmBurnSubtitleRef.current = onConfirmBurnSubtitle;
  });

  // Task 013: same pattern for the quality selection callback.
  const onLevelChangeRef = useRef(onLevelChange);
  useEffect(() => {
    onLevelChangeRef.current = onLevelChange;
  });

  // --- Sync media state from the <video> element via event listeners ---
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const readBuffered = () => {
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };
    const onPlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
    };
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDurationChange = () =>
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    const onProgress = () => readBuffered();
    const onVolumeChange = () => {
      setVolume(video.volume);
      setMuted(video.muted);
    };
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onRateChange = () => setPlaybackRate(video.playbackRate);

    // Seed state from the already-mounted element.
    setIsPlaying(!video.paused);
    setCurrentTime(video.currentTime);
    setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    setVolume(video.volume);
    setMuted(video.muted);
    setPlaybackRate(video.playbackRate);
    readBuffered();

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("durationchange", onDurationChange);
    video.addEventListener("loadedmetadata", onDurationChange);
    video.addEventListener("progress", onProgress);
    video.addEventListener("volumechange", onVolumeChange);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("ratechange", onRateChange);
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("durationchange", onDurationChange);
      video.removeEventListener("loadedmetadata", onDurationChange);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("volumechange", onVolumeChange);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("ratechange", onRateChange);
    };
  }, [videoRef]);

  // --- Restore player preferences from IndexedDB on mount ---
  // Volume, mute, and playback speed are global (same across videos).
  // Subtitle track selection is per-video. Restoring these means the user's
  // last-used settings carry across page reloads — zero server involvement.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    void (async () => {
      const [prefs, subtitleId] = await Promise.all([
        getGlobalPrefs(),
        getSubtitlePref(videoId),
      ]);
      if (cancelled) return;
      if (prefs) {
        video.volume = prefs.volume;
        video.muted = prefs.muted;
        video.playbackRate = prefs.playbackRate;
      }
      if (subtitleId) {
        const idx = subtitleTracks.findIndex((t) => t.id === subtitleId);
        if (idx >= 0) {
          setActiveSubtitle(idx);
          // Propagate the restored selection so the watch page can re-route
          // an ASS track to the libass canvas overlay on page reload.
          onActiveSubtitleChangeRef.current?.(idx);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [videoRef, videoId, subtitleTracks]);

  // --- Persist global prefs (debounced) ---
  // Fires when volume/mute/speed change; debounced 300ms so dragging the
  // volume slider doesn't spam IndexedDB writes.
  const prefsSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    prefsSaveTimer.current = setTimeout(() => {
      void saveGlobalPrefs({ volume, muted, playbackRate });
    }, 300);
    return () => {
      if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    };
  }, [volume, muted, playbackRate]);

  // --- Fullscreen state sync ---
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, [containerRef]);

  // --- Auto-hide: reveal on activity, hide after AUTO_HIDE_MS while playing ---
  // `revealRef` exposes the reveal routine (visible + fresh hide timer) so
  // the tap-to-toggle handler below reuses the exact same path as mousemove.
  const revealRef = useRef<() => void>(() => {});
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const reveal = () => {
      setControlsVisible(true);
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        if (isPlayingRef.current) setControlsVisible(false);
      }, AUTO_HIDE_MS);
    };
    revealRef.current = reveal;
    const onMouseMove = () => reveal();
    const onKeyDown = () => reveal();
    container.addEventListener("mousemove", onMouseMove);
    container.addEventListener("keydown", onKeyDown);
    reveal();
    return () => {
      container.removeEventListener("mousemove", onMouseMove);
      container.removeEventListener("keydown", onKeyDown);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [containerRef]);

  // Pausing always reveals controls (never auto-hides while paused).
  useEffect(() => {
    if (!isPlaying) setControlsVisible(true);
  }, [isPlaying]);

  // Keep controls (and the OS cursor) visible while the shortcuts overlay is
  // open so the user can read it and click its close affordance.
  useEffect(() => {
    if (showShortcuts) setControlsVisible(true);
  }, [showShortcuts]);

  // Hide the OS cursor over the container when controls are hidden. Done via
  // the DOM style API because the container is parent-owned and a dynamically
  // toggled Tailwind `cursor-none` class wouldn't be present in the JIT build.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.style.cursor = controlsVisible ? "" : "none";
  }, [controlsVisible, containerRef]);

  // --- Subtitle track mode application ---
  // `<track>` elements live in `video.textTracks` (order matches the non-ASS
  // tracks rendered by the parent — ASS tracks bypass `<track>` and route
  // through the libass canvas overlay instead). Mode "hidden" loads a track
  // without displaying; "showing" displays it. The native `default` attribute
  // is NOT used — custom controls drive mode explicitly (T8 removed `default`
  // from the <track>s).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Map the active full-list index to its position among the non-ASS tracks
    // (which are the only ones with a <track> element in video.textTracks).
    const nonAssTracks = subtitleTracks.filter((t) => !isAssFormat(t.format));
    const activeTrack =
      activeSubtitle >= 0 ? subtitleTracks[activeSubtitle] : null;
    const activeNonAssIndex = activeTrack
      ? nonAssTracks.findIndex((t) => t.id === activeTrack.id)
      : -1;
    const tracks = video.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = i === activeNonAssIndex ? "showing" : "hidden";
    }
  }, [activeSubtitle, videoRef, subtitleTracks]);

  // --- Settings menu click-outside ---
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!settingsOpen) return;
    const onDocDown = (e: MouseEvent) => {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(e.target as Node)
      ) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [settingsOpen]);

  // Controls stay visible while the settings menu is open (same rationale
  // as the shortcuts overlay below).
  useEffect(() => {
    if (settingsOpen) setControlsVisible(true);
  }, [settingsOpen]);

  // Opening the menu moves focus to its first enabled item (APG menu-button
  // pattern). jsdom renders synchronously; the portal menu is present after
  // this state commit, so query it fresh here.
  const settingsTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!settingsOpen) return;
    settingsMenuRef.current
      ?.querySelector<HTMLElement>('[role="menuitemradio"]:not(:disabled)')
      ?.focus();
  }, [settingsOpen]);

  // Arrow/Home/End navigation across the grouped radio items; Escape closes
  // and restores focus to the gear (APG menu contract, task 014).
  function handleMenuNav(e: React.KeyboardEvent<HTMLDivElement>) {
    const menu = settingsMenuRef.current;
    if (e.key === "Escape") {
      e.preventDefault();
      setSettingsOpen(false);
      settingsTriggerRef.current?.focus();
      return;
    }
    if (e.key === "Tab") {
      setSettingsOpen(false);
      return;
    }
    if (
      e.key !== "ArrowDown" &&
      e.key !== "ArrowUp" &&
      e.key !== "Home" &&
      e.key !== "End"
    ) {
      return;
    }
    e.preventDefault();
    const items = Array.from(
      menu?.querySelectorAll<HTMLElement>(
        '[role="menuitemradio"]:not(:disabled)',
      ) ?? [],
    );
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    let next: number;
    if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    else if (e.key === "ArrowDown") next = (current + 1) % items.length;
    else next = current <= 0 ? items.length - 1 : current - 1;
    items[next].focus();
  }

  // --- Polite announcements (FR-4.8) ---
  // A single aria-live=polite region announces media state changes. Each
  // source skips its mount-time value (no announcement on page load).
  const [announcement, setAnnouncement] = useState("");

  const prevPlaying = useRef(isPlaying);
  useEffect(() => {
    if (prevPlaying.current !== isPlaying) {
      prevPlaying.current = isPlaying;
      setAnnouncement(isPlaying ? "Playing" : "Paused");
    }
  }, [isPlaying]);

  const prevMuted = useRef(muted);
  useEffect(() => {
    if (prevMuted.current !== muted) {
      prevMuted.current = muted;
      setAnnouncement(muted ? "Muted" : "Unmuted");
    }
  }, [muted]);

  const prevSubtitle = useRef(activeSubtitle);
  useEffect(() => {
    if (prevSubtitle.current !== activeSubtitle) {
      prevSubtitle.current = activeSubtitle;
      setAnnouncement(
        activeSubtitle >= 0
          ? `Subtitles on: ${subtitleLabel(subtitleTracks[activeSubtitle])}`
          : "Subtitles off",
      );
    }
  }, [activeSubtitle, subtitleTracks]);

  const prevQuality = useRef<number | null>(qualitySelection);
  useEffect(() => {
    if (prevQuality.current !== qualitySelection) {
      prevQuality.current = qualitySelection;
      setAnnouncement(
        qualitySelection === null
          ? "Quality set to auto"
          : `Quality set to ${levels?.[qualitySelection]?.height ?? "?"}p`,
      );
    }
  }, [qualitySelection, levels]);

  // --- Tap-to-toggle-controls (FR-4.7) ---
  // DECISION (recorded per task 014): a tap on the video surface toggles the
  // controls and does NOT play/pause. The same surface serves scrubbing and
  // setting adjustments; an accidental pause is harder to undo on a touch
  // screen than a hidden control layer is to bring back. Keyboard and
  // pointer users keep the explicit Play/Pause button and Space/K.
  const controlsVisibleRef = useRef(controlsVisible);
  useEffect(() => {
    controlsVisibleRef.current = controlsVisible;
  }, [controlsVisible]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onPointerUp = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("button, input, [role='menu'], [role='dialog']"))
        return;
      if (controlsVisibleRef.current) {
        setControlsVisible(false);
      } else {
        revealRef.current();
      }
    };
    container.addEventListener("pointerup", onPointerUp);
    return () => container.removeEventListener("pointerup", onPointerUp);
  }, [containerRef]);

  // --- Handlers ---
  //
  // `video.play()` returns a Promise that rejects if the element has no
  // playable source — which can happen transiently while `/stream-url` is
  // still resolving (the parent renders this component before `src` lands)
  // or after an error path leaves `src` undefined. Without a `.catch()` the
  // rejection bubbles to the console as "Uncaught (in promise)
  // NotSupportedError: The element has no supported sources" — confusing
  // noise that looks like a real crash. Swallowing it is correct: if `src`
  // was empty because of a transient loading state, playback will start
  // automatically once `src` lands (the browser fires `play` then); if it
  // was a real error, the parent's `onError` handler surfaces it via the
  // `UnsupportedFormat` fallback.
  function safePlay(video: HTMLVideoElement) {
    void video.play().catch((err: unknown) => {
      // AbortError is benign — the user pressed play/pause quickly. All
      // other rejections are silenced here for the reason above; if the
      // state is genuinely broken, `onError` on the `<video>` will fire
      // and the parent will swap to the fallback UI.
      if (err instanceof DOMException && err.name === "AbortError") return;
    });
  }

  function togglePlay() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      safePlay(video);
    } else {
      video.pause();
    }
  }

  const seekBarRef = useRef<HTMLDivElement>(null);
  const [scrubbing, setScrubbing] = useState(false);

  function seekFromClientX(clientX: number) {
    const video = videoRef.current;
    const bar = seekBarRef.current;
    if (
      !video ||
      !bar ||
      !Number.isFinite(video.duration) ||
      video.duration <= 0
    )
      return;
    const rect = bar.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    setCurrentTime(video.currentTime);
  }

  // Pointer capture lets drag continue outside the bar; guard with try/catch
  // because some environments (jsdom) don't implement it.
  function onScrubberPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    setScrubbing(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture unsupported — drag stays within bounds */
    }
    seekFromClientX(e.clientX);
  }
  function onScrubberPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!scrubbing) return;
    seekFromClientX(e.clientX);
  }
  function onScrubberPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (!scrubbing) return;
    setScrubbing(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }
  function onScrubberKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    if (e.key === "ArrowLeft") {
      video.currentTime = Math.max(0, video.currentTime - SEEK_STEP_SECONDS);
      e.preventDefault();
    } else if (e.key === "ArrowRight") {
      video.currentTime = Math.min(
        video.duration,
        video.currentTime + SEEK_STEP_SECONDS,
      );
      e.preventDefault();
    }
  }

  function onVolumeInput(e: React.ChangeEvent<HTMLInputElement>) {
    const video = videoRef.current;
    if (!video) return;
    const v = Number(e.target.value);
    video.volume = v;
    if (v > 0 && video.muted) video.muted = false;
  }
  function toggleMute() {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
  }

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void containerRef.current?.requestFullscreen();
    }
  }

  // Image-family tracks can only render via server-side burn-in, which
  // requires the transcode pipeline. When `isTranscode` is false (or no
  // burn callback is wired), image tracks are disabled in the menu.
  const burnEnabled = isTranscode && !!onConfirmBurnSubtitle;

  const applySubtitleSelection = useCallback(
    (index: number) => {
      setActiveSubtitle(index);
      // Grouped settings: keep the menu open so the user can adjust several
      // settings in one sitting (closed via gear / Escape / outside click).
      // Notify the watch page so an ASS selection routes to the libass canvas
      // overlay (and Off / a non-ASS track tears it down).
      onActiveSubtitleChangeRef.current?.(index);
      // Persist per-video subtitle selection (null for "Off"). Image-family
      // tracks are NOT persisted — burn-in is per-session only (FR-5.4) and
      // shouldn't restore on reload (there's no guarantee the transcode cache
      // entry is still warm).
      const track = index >= 0 ? subtitleTracks[index] : null;
      if (!track || !isImageFamily(track)) {
        void saveSubtitlePref(videoId, track?.id ?? null);
      }
    },
    [subtitleTracks, videoId],
  );

  const selectSubtitle = useCallback(
    (index: number) => {
      const track = index >= 0 ? subtitleTracks[index] : null;
      // Image-family tracks during transcode require burn confirmation
      // (FR-5.2). Intercept the click and show the dialog instead of
      // selecting immediately.
      if (track && isImageFamily(track) && burnEnabled) {
        setPendingBurnIndex(index);
        setSettingsOpen(false);
        return;
      }
      applySubtitleSelection(index);
    },
    [subtitleTracks, burnEnabled, applySubtitleSelection],
  );

  function confirmBurn() {
    if (pendingBurnIndex === null) return;
    const track = subtitleTracks[pendingBurnIndex];
    setPendingBurnIndex(null);
    if (track) {
      // Notify the watch page to re-request the master playlist with
      // ?burn_subtitles={trackId} (Task 009).
      onConfirmBurnSubtitleRef.current?.(track.id);
    }
    applySubtitleSelection(pendingBurnIndex);
  }

  function cancelBurn() {
    setPendingBurnIndex(null);
  }

  function selectSpeed(rate: number) {
    const video = videoRef.current;
    if (video) video.playbackRate = rate;
    setPlaybackRate(rate);
  }

  function selectQuality(level: number) {
    setQualitySelection(level < 0 ? null : level);
    onLevelChangeRef.current?.(level);
  }

  // Task 014 (FR-4.6): the C shortcut cycles subtitles Off → first track →
  // next → … → Off. Routed through selectSubtitle so image-family tracks
  // still hit the burn confirmation and the IndexedDB pref stays in sync.
  const cycleSubtitle = useCallback(() => {
    const next =
      activeSubtitle + 1 >= subtitleTracks.length ? -1 : activeSubtitle + 1;
    selectSubtitle(next);
  }, [activeSubtitle, subtitleTracks, selectSubtitle]);

  // --- Global keyboard shortcuts (T9) ---
  // Re-subscribes whenever `cycleSubtitle`'s closure changes (the C shortcut
  // routes through selectSubtitle, which closes over the track list) — a
  // single cheap listener swap. Other shortcuts read refs / live DOM state.
  // Suppressed inside form fields and while a modifier key is held (same
  // pattern as the M2 `/` search shortcut).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable)
      ) {
        return;
      }
      // While the help overlay is open, only `?` (toggle) and Escape (close)
      // are honored — reading the help shouldn't drive playback.
      if (showShortcutsRef.current && e.key !== "?" && e.key !== "Escape") {
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      // Defer arrow seeking to the focused seek slider's own (±5s) handler —
      // a role=slider must answer arrows with its step (a11y convention).
      const isSlider =
        el instanceof Element && el.getAttribute("role") === "slider";
      switch (e.key) {
        case " ":
        case "Spacebar": // legacy IE key
        case "k":
        case "K":
          // Let a focused <button> activate natively (avoids double-toggle and
          // preserves screen-reader expectations for button activation).
          if (el && el.tagName === "BUTTON") return;
          e.preventDefault();
          if (video.paused) safePlay(video);
          else video.pause();
          break;
        case "ArrowLeft":
          if (isSlider) return;
          e.preventDefault();
          video.currentTime = Math.max(
            0,
            video.currentTime - SHORTCUT_SEEK_SECONDS,
          );
          setCurrentTime(video.currentTime);
          break;
        case "ArrowRight":
          if (isSlider) return;
          e.preventDefault();
          if (Number.isFinite(video.duration)) {
            video.currentTime = Math.min(
              video.duration,
              video.currentTime + SHORTCUT_SEEK_SECONDS,
            );
            setCurrentTime(video.currentTime);
          }
          break;
        case "j":
        case "J":
          e.preventDefault();
          video.currentTime = Math.max(
            0,
            video.currentTime - SHORTCUT_SEEK_SECONDS,
          );
          setCurrentTime(video.currentTime);
          break;
        case "l":
        case "L":
          e.preventDefault();
          if (Number.isFinite(video.duration)) {
            video.currentTime = Math.min(
              video.duration,
              video.currentTime + SHORTCUT_SEEK_SECONDS,
            );
            setCurrentTime(video.currentTime);
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          video.volume = Math.min(1, video.volume + SHORTCUT_VOLUME_STEP);
          if (video.muted) video.muted = false;
          break;
        case "ArrowDown":
          e.preventDefault();
          video.volume = Math.max(0, video.volume - SHORTCUT_VOLUME_STEP);
          break;
        case "f":
        case "F":
          e.preventDefault();
          if (document.fullscreenElement) void document.exitFullscreen();
          else void containerRef.current?.requestFullscreen();
          break;
        case "m":
        case "M":
          e.preventDefault();
          video.muted = !video.muted;
          break;
        case "c":
        case "C":
          e.preventDefault();
          cycleSubtitle();
          break;
        case "?":
          e.preventDefault();
          setShowShortcuts((v) => !v);
          break;
        case "Escape":
          setShowShortcuts(false);
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [videoRef, containerRef, cycleSubtitle]);

  // --- Derived ---
  const playRatio = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  const bufferedRatio = duration > 0 ? Math.min(1, buffered / duration) : 0;

  return (
    <>
      {/* Polite announcements (FR-4.8): playing/paused, muted, quality,
          subtitles. aria-live only — no role, so "status" queries that
          target the buffering spinner stay unambiguous. */}
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>

      {/* Buffering spinner — centered, shown on `waiting`, hidden on `playing`.
          Rendered outside the control bar so it stays visible when controls
          auto-hide. */}
      {isBuffering && (
        <div
          role="status"
          aria-label="Buffering"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/30 border-t-white" />
        </div>
      )}

      {/* Control bar */}
      <div
        className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-3 pt-10 transition-opacity duration-300 ${
          controlsVisible ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        {/* Seek bar */}
        <div
          ref={seekBarRef}
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration > 0 ? Math.floor(duration) : 0}
          aria-valuenow={Math.floor(currentTime)}
          aria-valuetext={
            duration > 0
              ? `${formatTime(currentTime)} elapsed, ${formatTime(
                  Math.max(0, duration - currentTime),
                )} remaining`
              : formatTime(currentTime)
          }
          onPointerDown={onScrubberPointerDown}
          onPointerMove={onScrubberPointerMove}
          onPointerUp={onScrubberPointerUp}
          onKeyDown={onScrubberKeyDown}
          className="group relative mb-2 h-3 cursor-pointer touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <div className="absolute top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full bg-white/20">
            {/* buffered */}
            <div
              className="absolute h-full rounded-full bg-white/30"
              style={{ width: `${bufferedRatio * 100}%` }}
            />
            {/* played */}
            <div
              className="absolute h-full rounded-full bg-[var(--color-castify-accent)]"
              style={{ width: `${playRatio * 100}%` }}
            />
          </div>
          {/* thumb */}
          <div
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            style={{ left: `${playRatio * 100}%` }}
          />
        </div>

        {/* Buttons row — primary transport larger (≥28px target), support
            controls ≥24px. */}
        <div className="flex items-center gap-1.5 text-white">
          {onPrev && (
            <Tooltip content="You're at the start of the queue.">
              <button
                type="button"
                onClick={() => {
                  if (!hasPrev) return;
                  onPrev();
                }}
                aria-disabled={hasPrev ? undefined : true}
                aria-label="Previous video"
                className={`rounded p-2 transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
                  hasPrev ? "" : "cursor-not-allowed opacity-30"
                }`}
              >
                <PrevIcon className="h-5 w-5" />
              </button>
            </Tooltip>
          )}

          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
            aria-pressed={isPlaying}
            className="rounded p-2 transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            {isPlaying ? (
              <PauseIcon className="h-7 w-7" />
            ) : (
              <PlayIcon className="h-7 w-7" />
            )}
          </button>

          {onNext && (
            <Tooltip content="You're at the end of the queue.">
              <button
                type="button"
                onClick={() => {
                  if (!hasNext) return;
                  onNext();
                }}
                aria-disabled={hasNext ? undefined : true}
                aria-label="Next video"
                className={`rounded p-2 transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
                  hasNext ? "" : "cursor-not-allowed opacity-30"
                }`}
              >
                <NextIcon className="h-5 w-5" />
              </button>
            </Tooltip>
          )}

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted || volume === 0 ? "Unmute" : "Mute"}
              aria-pressed={muted}
              className="rounded p-2 transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              {muted || volume === 0 ? (
                <VolumeMuteIcon className="h-5 w-5" />
              ) : (
                <VolumeHighIcon className="h-5 w-5" />
              )}
            </button>
            {/* Inline volume is a pointer/laptop affordance — below sm the
                bar would crowd at 360px, and touch users adjust volume with
                the device's own controls (standard mobile player behavior). */}
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={onVolumeInput}
              aria-label="Volume"
              aria-valuetext={`${Math.round((muted ? 0 : volume) * 100)}%`}
              className="hidden h-1 w-20 cursor-pointer accent-[var(--color-castify-accent)] sm:block"
            />
          </div>

          {/* Elapsed / remaining (−m:ss), per FR-4.2. */}
          <span className="ml-1 text-xs tabular-nums text-white/90">
            {formatTime(currentTime)} /{" "}
            {duration > 0
              ? `−${formatTime(Math.max(0, duration - currentTime))}`
              : formatTime(0)}
          </span>

          {/* Quiet quality badge — passive indicator; adjustments live in
              the Settings menu. Hidden below sm: the transport row is
              tight at phone widths (the Settings menu still shows quality). */}
          {levels && levels.length > 0 && (
            <span
              className="ml-1 hidden px-1 text-[0.65rem] uppercase tracking-wide text-white/50 sm:inline"
              aria-label={`Current quality ${
                (currentLevel ?? -1) < 0
                  ? "Auto"
                  : `${levels[currentLevel ?? 0]?.height ?? "?"}p`
              }`}
            >
              {(currentLevel ?? -1) < 0
                ? "Auto"
                : `${levels[currentLevel ?? 0]?.height ?? "?"}p`}
            </span>
          )}

          <div className="ml-auto flex items-center gap-1">
            {/* Task 013: the single Settings menu (FR-4.3) — grouped
                Quality / Speed / Subtitles sections with the stream mode in
                a quiet footer. Radio selections keep it open; Escape, the
                gear, or an outside click close it. */}
            <div ref={settingsMenuRef} className="relative">
              <button
                type="button"
                ref={settingsTriggerRef}
                onClick={() => setSettingsOpen((v) => !v)}
                aria-label="Settings"
                aria-haspopup="menu"
                aria-expanded={settingsOpen}
                onKeyDown={(e) => {
                  // APG menu-button: ArrowDown opens + focuses first item.
                  if (e.key === "ArrowDown" && !settingsOpen) {
                    e.preventDefault();
                    setSettingsOpen(true);
                  }
                }}
                className={`rounded p-2 transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
                  settingsOpen ? "bg-white/15" : ""
                }`}
              >
                <GearIcon className="h-5 w-5" />
              </button>
              {settingsOpen && (
                <div
                  role="menu"
                  aria-label="Settings"
                  onKeyDown={handleMenuNav}
                  className="absolute bottom-full right-0 mb-2 max-h-[70vh] w-56 overflow-y-auto rounded-md border border-white/10 bg-[var(--color-castify-surface)] py-1 text-sm text-white shadow-xl"
                >
                  {levels && levels.length > 0 && onLevelChange && (
                    <div role="none" className="border-b border-white/10 pb-1">
                      <p className="px-3 pb-1 pt-2 text-[0.65rem] font-semibold uppercase tracking-wide text-white/50">
                        Quality
                      </p>
                      <SettingsMenuItem
                        label={
                          (currentLevel ?? -1) >= 0
                            ? `Auto (current: ${levels[currentLevel ?? 0]?.height ?? "?"}p)`
                            : "Auto"
                        }
                        selected={qualitySelection === null}
                        onSelect={() => selectQuality(-1)}
                      />
                      {levels.map((l, i) => (
                        <SettingsMenuItem
                          key={`${l.height}-${i}`}
                          label={l.height > 0 ? `${l.height}p` : "Unknown"}
                          selected={qualitySelection === i}
                          onSelect={() => selectQuality(i)}
                        />
                      ))}
                    </div>
                  )}

                  <div role="none" className="border-b border-white/10 pb-1">
                    <p className="px-3 pb-1 pt-2 text-[0.65rem] font-semibold uppercase tracking-wide text-white/50">
                      Speed
                    </p>
                    {PLAYBACK_RATES.map((rate) => (
                      <SettingsMenuItem
                        key={rate}
                        label={`${rate}×`}
                        selected={playbackRate === rate}
                        onSelect={() => selectSpeed(rate)}
                      />
                    ))}
                  </div>

                  {subtitleTracks.length > 0 && (
                    <div role="none">
                      <p className="px-3 pb-1 pt-2 text-[0.65rem] font-semibold uppercase tracking-wide text-white/50">
                        Subtitles
                      </p>
                      <SettingsMenuItem
                        label="Off"
                        selected={activeSubtitle === -1}
                        onSelect={() => selectSubtitle(-1)}
                      />
                      {subtitleTracks.map((track, i) => (
                        <SettingsMenuItem
                          key={track.id}
                          label={subtitleLabel(track)}
                          selected={activeSubtitle === i}
                          disabled={isImageFamily(track) && !burnEnabled}
                          onSelect={() => selectSubtitle(i)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Stream mode lives here — quietly — not on the bar. */}
                  <p className="border-t border-white/10 px-3 py-1.5 text-[0.65rem] uppercase tracking-wide text-white/40">
                    {isTranscode ? "Transcoded" : "Direct stream"}
                  </p>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              aria-pressed={isFullscreen}
              className="rounded p-2 transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              {isFullscreen ? (
                <FullscreenExitIcon className="h-5 w-5" />
              ) : (
                <FullscreenEnterIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard shortcuts help overlay (T9, task 014: Dialog consumer —
          focus trap, Escape, labelled). Toggled by `?`, closed by `?` /
          Escape / backdrop click. The keydown handler suspends other
          shortcuts while this is open. */}
      <Dialog
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
        title="Keyboard shortcuts"
      >
        <dl className="grid grid-cols-[auto_1fr] items-center gap-x-5 gap-y-2 text-sm">
          <ShortcutsRow keys={["Space"]} desc="Play / Pause" />
          <ShortcutsRow keys={["K"]} desc="Play / Pause" />
          <ShortcutsRow keys={["←", "→"]} desc="Seek -10s / +10s" />
          <ShortcutsRow keys={["J", "L"]} desc="Seek -10s / +10s" />
          <ShortcutsRow keys={["↑", "↓"]} desc="Volume up / down" />
          <ShortcutsRow keys={["F"]} desc="Fullscreen" />
          <ShortcutsRow keys={["M"]} desc="Mute" />
          <ShortcutsRow keys={["C"]} desc="Cycle subtitles" />
          <ShortcutsRow keys={["?"]} desc="Show / hide this help" />
        </dl>
      </Dialog>

      {/* M8 Task 010: Burn-in confirmation dialog (FR-5.2 / FR-5.3), on the
          Dialog primitive since task 014. Shown when the user selects an
          image-family subtitle track during transcode playback. Confirming
          re-requests the transcode with `?burn_subtitles={trackId}`;
          cancelling leaves playback unchanged. */}
      <Dialog
        open={pendingBurnIndex !== null}
        onClose={cancelBurn}
        title="Burn subtitles"
        footer={
          <>
            <Button variant="secondary" onClick={cancelBurn}>
              Cancel
            </Button>
            <Button onClick={confirmBurn}>Yes</Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-castify-muted)]">
          Subtitles will be burned into the video. Playback may restart.
        </p>
        <p className="text-sm font-semibold">
          Restart playback with burned subtitles?
        </p>
      </Dialog>
    </>
  );
}

/** One radio row inside the grouped Settings menu. Selecting keeps the
 * menu open (grouped settings pattern) — Escape / gear / outside close. */
function SettingsMenuItem({
  label,
  selected,
  disabled = false,
  onSelect,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <div role="none">
      <button
        type="button"
        role="menuitemradio"
        aria-checked={selected}
        disabled={disabled}
        onClick={onSelect}
        className={`block w-full px-3 py-1.5 text-left transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent ${
          selected ? "text-[var(--color-castify-accent-dim)]" : "text-white"
        }`}
      >
        {label}
      </button>
    </div>
  );
}

function ShortcutsRow({ keys, desc }: { keys: string[]; desc: string }) {
  return (
    <>
      <dt className="flex items-center gap-1">
        {keys.map((k) => (
          <kbd
            key={k}
            className="rounded border border-white/20 bg-white/5 px-1.5 py-0.5 text-xs font-semibold"
          >
            {k}
          </kbd>
        ))}
      </dt>
      <dd className="text-white/70">{desc}</dd>
    </>
  );
}
